import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Cross-tab drill-down contract for the Transactions workspace. The
 * Actuals → P&L grid (and any future surface that wants to scope the
 * Transactions list down to one account × one month) builds a URL
 * with these search params, navigates to /financials/transactions,
 * and the LiveTransactionsTab + QboHistoryTab both read the same
 * shape and apply it as a pre-filter on top of their TanStack-table
 * state — same pattern as the post-bulk "Show failures" banner on
 * the Live tab.
 *
 * The contract is intentionally split across two parallel keys so
 * either tab can do its filtering with whatever id it actually owns:
 *
 *   accounts      — comma-separated chart_of_accounts.id. The Live
 *                   tab has `qb_account_id` on every row, so it
 *                   filters by exact id-set membership.
 *   from / to     — inclusive YYYY-MM-DD bounds on `posted_at`.
 *                   Both tabs apply the same bounds against their
 *                   own date column.
 *   accountLabel  — pretty label rendered in the "Drilled in from
 *                   P&L · <accountLabel>" banner; the value is
 *                   informational, never used for filtering.
 *   monthLabel    — pretty label for the date range (e.g. "Mar '26"
 *                   for a single month or "Jan '26 – Dec '26" for
 *                   the P&L "Total" column).
 *   source        — pretty name of the page the operator came from
 *                   ("Main Financials"); defaults to "P&L" in the banner.
 *   back          — in-app path (pathname + search) to return to. The
 *                   banner renders a "Back to <source>" button when
 *                   present. Only same-origin paths are honoured.
 *
 * The QBO history mirror has no FK back to chart_of_accounts — its
 * `split_account_name` is the QBO category as a plain string — so
 * the History tab maps the `accounts` ids to names via the
 * already-loaded chart_of_accounts hook at filter time. Same effect,
 * one URL contract.
 */

/**
 * Date the Mercury / Plaid live feed took over from the QBO ledger
 * mirror. Single source of truth used by the drill-down to route
 * P&L clicks to the right tab — single pre-cutover months go to
 * History, post-cutover (or ranges that cross the boundary) go to
 * Live. Hard-coded rather than read from config because every
 * other reference to the cutover in the codebase is a string
 * literal too (TransactionsPage description, the Live / History
 * tab JSDoc), and bumping it is a coordinated cross-file change.
 */
export const QBO_LIVE_CUTOVER_DATE = '2026-05-01';

/** URL search-param key names. Exported so callers can `delete` them
 *  by name when clearing the drill-down filter. */
export const TX_DRILLDOWN_PARAM_KEYS = [
  'accounts',
  'from',
  'to',
  'accountLabel',
  'monthLabel',
  'source',
  'back',
] as const;

/**
 * Accept only in-app paths for the `back` param so a crafted URL can't
 * bounce the operator to another origin.
 */
function sanitizeBackPath(raw: string | null): string | null {
  const v = (raw ?? '').trim();
  if (!v.startsWith('/') || v.startsWith('//') || v.includes('\\')) return null;
  return v;
}

export interface TxDrilldownFilter {
  /** chart_of_accounts.id set, parsed from the comma-separated
   *  `accounts` param. Empty when the param is absent. */
  accountIds: Set<string>;
  /** Inclusive lower bound on `posted_at`, YYYY-MM-DD, or null. */
  from: string | null;
  /** Inclusive upper bound on `posted_at`, YYYY-MM-DD, or null. */
  to: string | null;
  /** Pretty label for the account scope (banner display only). */
  accountLabel: string | null;
  /** Pretty label for the month scope (banner display only). */
  monthLabel: string | null;
  /** Pretty name of the originating page (banner display only). */
  source: string | null;
  /** In-app path to return to, or null when the operator arrived cold. */
  back: string | null;
  /** True iff any of accounts / from / to are set. */
  hasFilter: boolean;
}

export function parseTxDrilldownFilter(params: URLSearchParams): TxDrilldownFilter {
  const accountsRaw = (params.get('accounts') ?? '').trim();
  const accountIds = new Set<string>(
    accountsRaw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  );
  const from = (params.get('from') ?? '').trim() || null;
  const to = (params.get('to') ?? '').trim() || null;
  const accountLabel = (params.get('accountLabel') ?? '').trim() || null;
  const monthLabel = (params.get('monthLabel') ?? '').trim() || null;
  const source = (params.get('source') ?? '').trim() || null;
  const back = sanitizeBackPath(params.get('back'));
  const hasFilter = accountIds.size > 0 || !!from || !!to;
  return { accountIds, from, to, accountLabel, monthLabel, source, back, hasFilter };
}

/**
 * Hook variant of `parseTxDrilldownFilter` that also gives the caller
 * a stable `clear()` callback. The clear strips exactly the
 * drill-down keys and leaves everything else (tab, etc.) intact so
 * the operator stays on the same tab they landed on.
 *
 * The returned object reference is itself memoized so downstream
 * `useMemo` deps that key off `drilldown` (e.g. the pre-filtered row
 * list in LiveTransactionsTab) only refire when the underlying
 * params actually change.
 */
export function useTxDrilldownFilter(): TxDrilldownFilter & {
  clear: () => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();

  const filter = useMemo(() => parseTxDrilldownFilter(searchParams), [searchParams]);

  const clear = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    for (const key of TX_DRILLDOWN_PARAM_KEYS) next.delete(key);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return useMemo(() => ({ ...filter, clear }), [filter, clear]);
}

export interface BuildTxDrilldownUrlArgs {
  /** chart_of_accounts.id list — usually the clicked leaf, or every
   *  leaf descendant when the click was on a parent / subtotal row. */
  accountIds: string[];
  /** Pretty banner label for the account scope. */
  accountLabel: string;
  /** Inclusive YYYY-MM-DD lower bound on `posted_at`. */
  from: string;
  /** Inclusive YYYY-MM-DD upper bound on `posted_at`. */
  to: string;
  /** Pretty banner label for the date range (e.g. "Mar '26"). */
  monthLabel: string;
  /** Force a tab. Defaults to smart-routing: if the range ends before
   *  the QBO → Live cutover the URL points at the History tab,
   *  otherwise it points at the Live tab. Useful for the "Total"
   *  column on the P&L which spans multiple months and should always
   *  land on Live (where the actionable post-cutover data lives). */
  tab?: 'live' | 'history';
  /** Name of the originating page, shown in the banner ("Main Financials"). */
  source?: string;
  /** In-app path (pathname + search) the banner's Back button returns to. */
  back?: string;
}

/**
 * Build the deep-link URL into the Transactions page for a single
 * (account, month) scope. The route + tab routing live here so both
 * the P&L Actuals grid and any future drill-down site (e.g. the
 * Balance Sheet drill-down to register transactions) share the
 * same URL shape and the same cutover routing rule.
 */
export function buildTxDrilldownUrl(args: BuildTxDrilldownUrlArgs): string {
  const tab = args.tab ?? (args.to < QBO_LIVE_CUTOVER_DATE ? 'history' : 'live');
  const params = new URLSearchParams();
  params.set('tab', tab);
  params.set('accounts', args.accountIds.join(','));
  params.set('accountLabel', args.accountLabel);
  params.set('from', args.from);
  params.set('to', args.to);
  params.set('monthLabel', args.monthLabel);
  if (args.source) params.set('source', args.source);
  if (args.back) params.set('back', args.back);
  return `/financials/transactions?${params.toString()}`;
}

/**
 * True iff `postedAt` (as returned by the DB — ISO timestamp or
 * YYYY-MM-DD) falls within [from, to] inclusive. `from` / `to` are
 * the YYYY-MM-DD bounds from the drill-down filter; null bounds
 * are treated as open-ended so a caller with only `from` (or
 * only `to`) still filters correctly.
 *
 * The `to` bound is inclusive of the entire calendar day in UTC —
 * timestamps with a 23:59 wall-clock posted_at must still match,
 * which is the operator's expectation for "this transaction is
 * in March '26".
 */
export function isWithinDrilldownRange(
  postedAt: string,
  from: string | null,
  to: string | null,
): boolean {
  if (!from && !to) return true;
  const ts = new Date(postedAt).getTime();
  if (from) {
    const fromMs = new Date(`${from}T00:00:00.000Z`).getTime();
    if (ts < fromMs) return false;
  }
  if (to) {
    const toMs = new Date(`${to}T23:59:59.999Z`).getTime();
    if (ts > toMs) return false;
  }
  return true;
}
